/**
 * Business Objective Workshop: 10-screen artifact-native flow
 * SessionSetup → ObjectiveDefinition → VisionAndWhy → ReportingPeriods → TimelineConfidence
 * → Delays → AccelerationModel → ReadinessGate → [ObjectiveSummary|CommitmentToll] → WhatHappensNext
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Checkbox,
  message,
  Spin,
  Alert,
  Typography,
  Progress,
  Steps,
  Form,
  Input,
  InputNumber,
  Slider,
  Select,
} from 'antd';
import styles from '../components/BridgeCard/BridgeCard.module.css';
import { ArrowLeftOutlined, CheckOutlined, MailOutlined } from '@ant-design/icons';
import { isLocalBridgeApiEnabled } from '../services/bridgeSessionDataAdapter';
import {
  getWorkshopObjective,
  advanceWorkshopScreen,
  createWorkshopBusinessPeriod,
  updateWorkshopBusinessPeriod,
  createWorkshopVision,
  updateWorkshopVision,
  createWorkshopDelay,
  updateWorkshopDelay,
  deleteWorkshopDelay,
  updateWorkshopObjective,
  updateBridgeSession,
  getWorkshopDomainConfig,
  DELAY_CATEGORIES,
  type WorkshopObjectiveResponse,
  type DomainConfig,
} from '../services/localBridgeSessionApi';
const { Text } = Typography;
const { TextArea } = Input;

/** Date range helpers for reporting periods */
function getPeriodRanges(periodType: 'LAST_YEAR' | 'LAST_QUARTER' | 'LAST_MONTH') {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth(); // 0-indexed

  if (periodType === 'LAST_YEAR') {
    const lastYear = year - 1;
    return {
      current: {
        start: `${lastYear}-01-01`,
        end: `${lastYear}-12-31`,
        label: `Last Year (${lastYear})`,
      },
      target: {
        start: `${year}-01-01`,
        end: `${year}-12-31`,
        label: `This Year (${year})`,
      },
    };
  }
  if (periodType === 'LAST_QUARTER') {
    const lastQ = month < 3 ? 4 : Math.floor(month / 3);
    const lastQYear = month < 3 ? year - 1 : year;
    const qStartMonth = (lastQ - 1) * 3;
    const qEndMonth = qStartMonth + 2;
    const lastQStart = `${lastQYear}-${String(qStartMonth + 1).padStart(2, '0')}-01`;
    const lastQEnd = new Date(lastQYear, qEndMonth + 1, 0);
    const lastQEndStr = `${lastQYear}-${String(qEndMonth + 1).padStart(2, '0')}-${String(lastQEnd.getDate()).padStart(2, '0')}`;

    const thisQ = month < 3 ? 1 : Math.floor(month / 3) + 1;
    const thisQYear = month < 3 ? year : year;
    const thisQStartMonth = (thisQ - 1) * 3;
    const thisQEndMonth = thisQStartMonth + 2;
    const thisQStart = `${thisQYear}-${String(thisQStartMonth + 1).padStart(2, '0')}-01`;
    const thisQEnd = new Date(thisQYear, thisQEndMonth + 1, 0);
    const thisQEndStr = `${thisQYear}-${String(thisQEndMonth + 1).padStart(2, '0')}-${String(thisQEnd.getDate()).padStart(2, '0')}`;

    return {
      current: {
        start: lastQStart,
        end: lastQEndStr,
        label: `Last Quarter (Q${lastQ} ${lastQYear})`,
      },
      target: {
        start: thisQStart,
        end: thisQEndStr,
        label: `This Quarter (Q${thisQ} ${thisQYear})`,
      },
    };
  }
  // LAST_MONTH
  const lastMonthDate = new Date(year, month - 1, 1);
  const lastMonthStr = lastMonthDate.toLocaleString('default', { month: 'short' });
  const lastMonthYear = lastMonthDate.getFullYear();
  const lastMonthStart = `${lastMonthYear}-${String(lastMonthDate.getMonth() + 1).padStart(2, '0')}-01`;
  const lastMonthEnd = new Date(lastMonthYear, lastMonthDate.getMonth() + 1, 0);
  const lastMonthEndStr = `${lastMonthYear}-${String(lastMonthEnd.getMonth() + 1).padStart(2, '0')}-${String(lastMonthEnd.getDate()).padStart(2, '0')}`;

  const thisMonthStart = `${year}-${String(month + 1).padStart(2, '0')}-01`;
  const thisMonthEnd = new Date(year, month + 1, 0);
  const thisMonthEndStr = `${year}-${String(thisMonthEnd.getMonth() + 1).padStart(2, '0')}-${String(thisMonthEnd.getDate()).padStart(2, '0')}`;

  return {
    current: {
      start: lastMonthStart,
      end: lastMonthEndStr,
      label: `Last Month (${lastMonthStr} ${lastMonthYear})`,
    },
    target: {
      start: thisMonthStart,
      end: thisMonthEndStr,
      label: `This Month (${now.toLocaleString('default', { month: 'short' })} ${year})`,
    },
  };
}

const OBJECTIVE_DOMAIN_OPTIONS = [
  { value: 'Revenue growth', label: 'Revenue growth' },
  { value: 'Earnings growth', label: 'Earnings growth' },
  { value: 'Expense Control', label: 'Expense Control' },
  { value: 'Business Exit', label: 'Business Exit' },
  { value: 'New product', label: 'New product' },
  { value: 'Business stability', label: 'Business stability' },
  { value: 'Vision Realization', label: 'Vision Realization' },
];

const BASE_SCREEN_TITLES = [
  'Session Setup',
  'Objective Definition',
  'Vision & Why',
  'Reporting Periods',
  'Timeline & Confidence',
  'Delays',
  'Acceleration Model',
  'Readiness Gate',
  'Objective Summary',
  'Control Bridge',
  'Commitment Toll',
  'What Happens Next',
];

function getSelectedDomains(objectiveDomain: string | null | undefined): string[] {
  if (!objectiveDomain) return [];
  return String(objectiveDomain)
    .split(',')
    .map((d) => d.trim())
    .filter(Boolean);
}

/** In-session fallback when workshop advance did not yet persist domain strategies to the API. */
function domainStrategiesFromFormValues(
  formValues: Record<string, unknown>,
  objectiveDomain: string | null | undefined
): WorkshopObjectiveResponse['domain_strategies'] {
  const domains = getSelectedDomains(objectiveDomain);
  if (domains.length === 0) return [];
  const out: NonNullable<WorkshopObjectiveResponse['domain_strategies']> = [];
  for (const domain of domains) {
    const stored = formValues[`domain_strategy_${domain}`] as Record<string, unknown> | undefined;
    const strategies = Array.isArray(stored?.strategies) ? (stored.strategies as string[]) : [];
    if (strategies.length === 0 && !stored) continue;
    out.push({
      id: domain,
      objective_id: '',
      domain,
      strategies,
      launch_date: (stored?.launch_date as string | null) ?? null,
      target_exit_date: (stored?.target_exit_date as string | null) ?? null,
      current_vision: (stored?.current_vision as string | null) ?? null,
      emotional_impact: (stored?.emotional_impact as string | null) ?? null,
      target_revenue: (stored?.target_revenue as number | null) ?? null,
      target_valuation: (stored?.target_valuation as number | null) ?? null,
    });
  }
  return out.length > 0 ? out : undefined;
}

function getScreenTitles(selectedDomains: string[]): string[] {
  if (selectedDomains.length === 0) return BASE_SCREEN_TITLES;
  const domainTitles = selectedDomains.map((d) => `${d} Strategy`);
  return [
    BASE_SCREEN_TITLES[0],
    BASE_SCREEN_TITLES[1],
    ...domainTitles,
    ...BASE_SCREEN_TITLES.slice(2),
  ];
}

export const BusinessObjectiveWorkshopPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('sessionId');
  const navigate = useNavigate();

  const [data, setData] = useState<WorkshopObjectiveResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();
  const [formValues, setFormValues] = useState<Record<string, unknown>>({});
  const [backHistory, setBackHistory] = useState<number[]>([]);
  const [domainConfig, setDomainConfig] = useState<DomainConfig | null>(null);

  const objective = data?.objective ?? null;
  const screenIndex = objective?.screen_index ?? 0;
  const selectedDomains = getSelectedDomains(objective?.objective_domain);
  const screenTitles = getScreenTitles(selectedDomains);
  const nDomains = selectedDomains.length;
  const isDomainScreen = nDomains > 0 && screenIndex >= 2 && screenIndex < 2 + nDomains;
  const domainScreenIndex = isDomainScreen ? screenIndex - 2 : -1;
  const currentDomain = isDomainScreen ? selectedDomains[domainScreenIndex] : null;

  const loadData = useCallback(async (options?: { silent?: boolean }) => {
    if (!sessionId || !isLocalBridgeApiEnabled()) {
      setData(null);
      setLoading(false);
      return;
    }
    if (!options?.silent) setLoading(true);
    try {
      const d = await getWorkshopObjective(sessionId);
      setData(d ?? null);
      if (d?.objective) {
        const obj = d.objective;
        form.setFieldsValue({
          business_name: obj.business_id ? '' : '',
          decision_authority_present: obj.decision_authority_present,
          objective_description: obj.objective_description,
          objective_domain: obj.objective_domain,
          target_date: obj.target_date,
          vision_description: d.vision?.vision_description,
          time_horizon_months: d.vision?.time_horizon_months,
          emotional_why: obj.emotional_why,
          emotional_importance_score: obj.emotional_importance_score ?? 5,
          estimated_months_to_goal: obj.estimated_months_to_goal,
          confidence_score: obj.confidence_score ?? 5,
          potential_months_saved: obj.potential_months_saved,
          timeline_shortening_importance: obj.timeline_shortening_importance,
          workshop_participants: obj.workshop_participants,
          workshop_datetime: obj.workshop_datetime,
        });
        setFormValues((prev) => ({
          ...prev,
          decision_authority_present: obj.decision_authority_present,
          objective_description: obj.objective_description,
          objective_domain: obj.objective_domain,
          target_date: obj.target_date,
          vision_description: d.vision?.vision_description,
          emotional_why: obj.emotional_why,
          emotional_importance_score: obj.emotional_importance_score ?? 5,
          estimated_months_to_goal: obj.estimated_months_to_goal,
          confidence_score: obj.confidence_score ?? 5,
          potential_months_saved: obj.potential_months_saved,
          timeline_shortening_importance: obj.timeline_shortening_importance,
          workshop_participants: obj.workshop_participants,
          workshop_datetime: obj.workshop_datetime,
        }));
      }
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [sessionId, form]);

  const refreshData = useCallback(() => loadData({ silent: true }), [loadData]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Redirect to Control Bridge when at domain selection screen (9+N)
  useEffect(() => {
    if (!sessionId || !data?.objective) return;
    const idx = data.objective.screen_index ?? 0;
    const controlBridgeIdx = 9 + getSelectedDomains(data.objective.objective_domain).length;
    if (idx === controlBridgeIdx) {
      navigate(`/local-bridge/control-bridge?sessionId=${sessionId}`);
    }
  }, [sessionId, data?.objective?.screen_index, data?.objective?.objective_domain, navigate]);

  useEffect(() => {
    getWorkshopDomainConfig().then(setDomainConfig);
  }, []);

  // Initialize back history when loading on a screen > 0 (e.g. returning to in-progress workshop)
  useEffect(() => {
    const idx = data?.objective?.screen_index ?? 0;
    if (idx > 0 && backHistory.length === 0) {
      setBackHistory(Array.from({ length: idx }, (_, i) => i));
    }
  }, [data?.objective?.screen_index, backHistory.length]);

  const pushBack = useCallback(() => {
    setBackHistory((h) => [...h, screenIndex]);
  }, [screenIndex]);

  const handleBack = useCallback(() => {
    const prev = backHistory[backHistory.length - 1];
    if (prev == null) return;
    setBackHistory((h) => h.slice(0, -1));
    setSubmitting(true);
    updateWorkshopObjective(sessionId!, { screen_index: prev })
      .then((d) => {
        if (d) setData(d);
      })
      .finally(() => setSubmitting(false));
  }, [backHistory, sessionId]);

  const handleAdvance = useCallback(
    async (response: Record<string, unknown>) => {
      if (!sessionId) return;
      setSubmitting(true);
      try {
        pushBack();
        const updated = await advanceWorkshopScreen(sessionId, response);
        if (updated) {
          setData(updated);
          const newIdx = updated.objective?.screen_index ?? 0;
          const controlBridgeIdx = 9 + nDomains;
          if (newIdx === controlBridgeIdx) {
            navigate(`/local-bridge/control-bridge?sessionId=${sessionId}`);
            return;
          }
        } else {
          message.error('Failed to save');
        }
      } catch {
        message.error('Failed to save');
      } finally {
        setSubmitting(false);
      }
    },
    [sessionId, pushBack, nDomains, navigate]
  );

  const handleContinueToBridge = useCallback(async () => {
    if (!sessionId) return;
    setSubmitting(true);
    try {
      await updateBridgeSession(sessionId, { status: 'collecting_current' });
      message.success('Workshop complete. Starting Bridge Session.');
      navigate(`/bridge-session/${sessionId}?step=questions`);
    } catch {
      message.error('Failed to continue');
    } finally {
      setSubmitting(false);
    }
  }, [sessionId, navigate]);

  if (!isLocalBridgeApiEnabled()) {
    return (
      <div style={{ padding: 24 }}>
        <Alert type="info" message="Local Bridge API not configured" />
        <Link to="/local-bridge">
          <Button icon={<ArrowLeftOutlined />} style={{ marginTop: 16 }}>
            Back to Local Bridge
          </Button>
        </Link>
      </div>
    );
  }

  if (!sessionId) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          type="warning"
          message="No session"
          description="Session ID is required. Create a session from Local Bridge."
        />
        <Link to="/local-bridge">
          <Button icon={<ArrowLeftOutlined />} style={{ marginTop: 16 }}>
            Back to Local Bridge
          </Button>
        </Link>
      </div>
    );
  }

  if (loading || !data) {
    return (
      <div style={{ padding: 24 }}>
        <Spin tip="Loading workshop..." />
      </div>
    );
  }

  const totalSteps = screenTitles.length;
  const progressPercent = totalSteps > 1 ? Math.round((screenIndex / (totalSteps - 1)) * 100) : 0;
  const prevTitle = screenIndex > 0 ? screenTitles[screenIndex - 1] : null;
  const currentTitle = screenTitles[screenIndex] ?? `Screen ${screenIndex}`;
  const nextTitle = screenIndex < totalSteps - 1 ? screenTitles[screenIndex + 1] : null;
  const canGoBack = backHistory.length > 0;

  return (
    <div style={{ padding: 24, maxWidth: 720, margin: '0 auto' }}>
      <Link to="/local-bridge">
        <Button icon={<ArrowLeftOutlined />} style={{ marginBottom: 16 }}>
          Back to Local Bridge
        </Button>
      </Link>

      <Typography.Title level={4}>Business Objective Workshop</Typography.Title>

      <Progress percent={progressPercent} showInfo={false} strokeColor="#1890ff" style={{ marginBottom: 12 }} />

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          marginBottom: 24,
          minHeight: 32,
        }}
      >
        <div style={{ flex: 1, textAlign: 'left' }}>
          {prevTitle ? (
            canGoBack ? (
              <Text
                type="secondary"
                style={{
                  fontSize: 13,
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  opacity: submitting ? 0.6 : 1,
                }}
                onClick={() => !submitting && handleBack()}
              >
                ← {prevTitle}
              </Text>
            ) : (
              <Text type="secondary" style={{ fontSize: 13 }}>
                {prevTitle}
              </Text>
            )
          ) : (
            <span />
          )}
        </div>
        <Text strong style={{ flex: '0 0 auto', fontSize: 15 }}>
          {currentTitle}
        </Text>
        <div style={{ flex: 1, textAlign: 'right' }}>
          {nextTitle ? (
            <Text type="secondary" style={{ fontSize: 13 }}>
              {nextTitle} →
            </Text>
          ) : (
            <span />
          )}
        </div>
      </div>

      <Card>

        {screenIndex === 0 && (
          <Screen0SessionSetup
            data={data}
            formValues={formValues}
            setFormValues={setFormValues}
            onAdvance={handleAdvance}
            submitting={submitting}
          />
        )}
        {screenIndex === 1 && (
          <Screen1ObjectiveDefinition
            data={data}
            formValues={formValues}
            setFormValues={setFormValues}
            onAdvance={handleAdvance}
            submitting={submitting}
          />
        )}
        {isDomainScreen && currentDomain && (
          <ScreenDomainStrategy
            domain={currentDomain}
            domainIndex={domainScreenIndex}
            totalDomains={nDomains}
            data={data}
            domainConfig={domainConfig}
            formValues={formValues}
            setFormValues={setFormValues}
            onAdvance={handleAdvance}
            submitting={submitting}
          />
        )}
        {!isDomainScreen && screenIndex === 2 + nDomains && (
          <Screen2VisionAndWhy
            data={data}
            sessionId={sessionId!}
            formValues={formValues}
            setFormValues={setFormValues}
            onAdvance={handleAdvance}
            submitting={submitting}
          />
        )}
        {!isDomainScreen && screenIndex === 3 + nDomains && (
          <Screen3ReportingPeriods
            data={data}
            sessionId={sessionId!}
            formValues={formValues}
            setFormValues={setFormValues}
            onAdvance={handleAdvance}
            submitting={submitting}
          />
        )}
        {!isDomainScreen && screenIndex === 4 + nDomains && (
          <Screen4TimelineConfidence
            data={data}
            formValues={formValues}
            setFormValues={setFormValues}
            onAdvance={handleAdvance}
            submitting={submitting}
          />
        )}
        {!isDomainScreen && screenIndex === 5 + nDomains && (
          <Screen5Delays
            data={data}
            sessionId={sessionId!}
            refreshData={refreshData}
            formValues={formValues}
            setFormValues={setFormValues}
            onAdvance={handleAdvance}
            submitting={submitting}
          />
        )}
        {!isDomainScreen && screenIndex === 6 + nDomains && (
          <Screen6AccelerationModel
            sessionId={sessionId!}
            data={data}
            loadData={refreshData}
            formValues={formValues}
            setFormValues={setFormValues}
            onAdvance={handleAdvance}
            submitting={submitting}
          />
        )}
        {!isDomainScreen && screenIndex === 7 + nDomains && (
          <Screen7ReadinessGate
            data={data}
            formValues={formValues}
            setFormValues={setFormValues}
            onAdvance={handleAdvance}
            submitting={submitting}
          />
        )}
        {!isDomainScreen && screenIndex === 8 + nDomains && (
          <Screen8ObjectiveSummary
            data={data}
            sessionId={sessionId!}
            onContinue={handleContinueToBridge}
            submitting={submitting}
          />
        )}
        {!isDomainScreen && screenIndex === 10 + nDomains && (
          <Screen9CommitmentToll
            sessionId={sessionId!}
            data={data}
            loadData={refreshData}
            formValues={formValues}
            setFormValues={setFormValues}
            onAdvance={handleAdvance}
            submitting={submitting}
          />
        )}
        {!isDomainScreen && screenIndex === 11 + nDomains && (
          <Screen10WhatHappensNext sessionId={sessionId!} onContinue={handleContinueToBridge} submitting={submitting} />
        )}
      </Card>
    </div>
  );
};

const WELCOME_TEXT =
  'Welcome to the Business Objective Workshop. In this 20 min session we will take the first step in defining your business objective. Having a clearly defined destination is the first step to accelerating performance. At the end of this session we will identify the objective and the value it is bringing to the business.';

function Screen0SessionSetup({
  data,
  formValues,
  setFormValues,
  onAdvance,
  submitting,
}: {
  data: WorkshopObjectiveResponse;
  formValues: Record<string, unknown>;
  setFormValues: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
  onAdvance: (r: Record<string, unknown>) => void;
  submitting: boolean;
}) {
  // Merge local edits with server state — loadData updates `form` but not parent `formValues`,
  // so authority must fall back to objective or Continue stays disabled / sends undefined.
  const authorityFromForm = formValues.decision_authority_present;
  const authorityFromServer = data?.objective?.decision_authority_present;
  const rawAuthority =
    authorityFromForm !== undefined ? authorityFromForm : authorityFromServer;
  const decisionAuthorityPresent: boolean | undefined =
    rawAuthority === true || rawAuthority === false ? rawAuthority : undefined;
  const hasDecision = typeof decisionAuthorityPresent === 'boolean';

  const companyName = data?.company_name ?? '';
  const participants =
    (formValues.workshop_participants as string) ??
    data?.objective?.workshop_participants ??
    '';
  const workshopDatetime =
    (formValues.workshop_datetime as string) ??
    data?.objective?.workshop_datetime ??
    '';

  return (
    <>
      <Text style={{ display: 'block', marginBottom: 24 }}>{WELCOME_TEXT}</Text>

      <div style={{ marginBottom: 20 }}>
        <Text strong style={{ display: 'block', marginBottom: 4 }}>
          Company
        </Text>
        <Text>{companyName || '—'}</Text>
      </div>

      <Form layout="vertical" style={{ marginBottom: 20 }}>
        <Form.Item label="Workshop participants">
          <Input
            value={participants}
            onChange={(e) =>
              setFormValues((p) => ({ ...p, workshop_participants: e.target.value }))
            }
            placeholder="e.g. John Smith (CEO), Jane Doe (COO)"
          />
        </Form.Item>
        <Form.Item label="Workshop time / date">
          <Input
            type="datetime-local"
            value={workshopDatetime ? workshopDatetime.slice(0, 16) : ''}
            onChange={(e) =>
              setFormValues((p) => ({ ...p, workshop_datetime: e.target.value || null }))
            }
          />
        </Form.Item>
      </Form>

      <Text style={{ display: 'block', marginBottom: 16 }}>
        Is someone with decision authority present?
      </Text>
      <div style={{ marginBottom: 16 }}>
        <Button
          type={decisionAuthorityPresent === true ? 'primary' : 'default'}
          style={{ marginRight: 8 }}
          onClick={() => {
            setFormValues((p) => ({ ...p, decision_authority_present: true }));
          }}
        >
          Yes
        </Button>
        <Button
          type={decisionAuthorityPresent === false ? 'primary' : 'default'}
          onClick={() => {
            setFormValues((p) => ({ ...p, decision_authority_present: false }));
          }}
        >
          No
        </Button>
      </div>
      <Button
        type="primary"
        onClick={() => {
          if (decisionAuthorityPresent !== true && decisionAuthorityPresent !== false) {
            return;
          }
          onAdvance({
            decision_authority_present: decisionAuthorityPresent,
            workshop_participants: participants || null,
            workshop_datetime: workshopDatetime || null,
          });
        }}
        loading={submitting}
        disabled={!hasDecision}
      >
        Continue
      </Button>
    </>
  );
}

function Screen1ObjectiveDefinition({
  data,
  formValues,
  setFormValues,
  onAdvance,
  submitting,
}: {
  data: WorkshopObjectiveResponse;
  formValues: Record<string, unknown>;
  setFormValues: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
  onAdvance: (r: Record<string, unknown>) => void;
  submitting: boolean;
}) {
  return (
    <>
      <Text style={{ display: 'block', marginBottom: 16 }}>
        Let's start with the end in mind. What does success look like?
      </Text>
      <Form layout="vertical" style={{ marginBottom: 16 }}>
        <Form.Item label="Objective description" required>
          <TextArea
            rows={3}
            value={String(formValues.objective_description ?? data?.objective?.objective_description ?? '')}
            onChange={(e) => setFormValues((p) => ({ ...p, objective_description: e.target.value }))}
            placeholder="e.g. Reach $2M ARR with positive unit economics"
          />
        </Form.Item>
        <Form.Item label="Objective domain" required>
          <Select
            mode="multiple"
            placeholder="Select one or more domains"
            options={OBJECTIVE_DOMAIN_OPTIONS}
            value={
              Array.isArray(formValues.objective_domain)
                ? (formValues.objective_domain as string[])
                : (() => {
                    const raw = formValues.objective_domain ?? data?.objective?.objective_domain ?? '';
                    if (!raw) return [];
                    const parts = String(raw).split(',').map((s) => s.trim()).filter(Boolean);
                    return parts.filter((p) => OBJECTIVE_DOMAIN_OPTIONS.some((o) => o.value === p)).length > 0
                      ? parts.filter((p) => OBJECTIVE_DOMAIN_OPTIONS.some((o) => o.value === p))
                      : parts;
                  })()
            }
            onChange={(v) => setFormValues((p) => ({ ...p, objective_domain: v }))}
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Form.Item label="Target date" required>
          <Input
            type="date"
            value={String(formValues.target_date ?? data?.objective?.target_date ?? '').slice(0, 10)}
            onChange={(e) => setFormValues((p) => ({ ...p, target_date: e.target.value }))}
          />
        </Form.Item>
      </Form>
      <Button
        type="primary"
        onClick={() => {
          const domains = formValues.objective_domain ?? data?.objective?.objective_domain;
          const domainValue = Array.isArray(domains) ? domains.join(', ') : domains;
          onAdvance({
            objective_description: formValues.objective_description ?? data?.objective?.objective_description,
            objective_domain: domainValue,
            target_date: formValues.target_date ?? data?.objective?.target_date,
          });
        }}
        loading={submitting}
        disabled={
          !(formValues.objective_description ?? data?.objective?.objective_description) ||
          (() => {
            const d = formValues.objective_domain ?? data?.objective?.objective_domain;
            return Array.isArray(d) ? d.length === 0 : !String(d ?? '').trim();
          })() ||
          !(formValues.target_date ?? data?.objective?.target_date)
        }
      >
        Continue
      </Button>
    </>
  );
}

const FALLBACK_DOMAIN_STRATEGIES: Record<string, Array<{ value: string; label: string }>> = {
  'Revenue growth': [
    { value: 'Increase Sales Conversion', label: 'Increase Sales Conversion' },
    { value: 'Improve Messaging', label: 'Improve Messaging' },
    { value: 'Increase Sales capacity', label: 'Increase Sales capacity' },
    { value: 'Add product', label: 'Add product' },
    { value: 'Expand Market', label: 'Expand Market' },
  ],
  'Earnings growth': [
    { value: 'Increase Revenue', label: 'Increase Revenue' },
    { value: 'Reduce Spending', label: 'Reduce Spending' },
    { value: 'Increase Efficiency', label: 'Increase Efficiency' },
  ],
  'New product': [],
  'Business stability': [
    { value: 'Stabilize Revenue', label: 'Stabilize Revenue' },
    { value: 'Control Expenses', label: 'Control Expenses' },
    { value: 'Increase Efficiency', label: 'Increase Efficiency' },
  ],
  'Expense Control': [
    { value: 'Financial Discipline', label: 'Financial Discipline' },
    { value: 'Increase Efficiency', label: 'Increase Efficiency' },
    { value: 'Supplier realignment', label: 'Supplier realignment' },
  ],
  'Business Exit': [
    { value: 'M&A', label: 'M&A' },
    { value: 'IPO', label: 'IPO' },
    { value: 'Employee Buyout', label: 'Employee Buyout' },
    { value: 'Other', label: 'Other' },
  ],
  'Vision Realization': [
    { value: 'Vision alignment with business', label: 'Vision alignment with business' },
    { value: 'New Product', label: 'New Product' },
    { value: 'Improved execution', label: 'Improved execution' },
  ],
};

const FALLBACK_DOMAIN_FIELDS: Record<string, Array<{ key: string; label: string; type: string }>> = {
  'Revenue growth': [],
  'Earnings growth': [],
  'New product': [
    { key: 'launch_date', label: 'Launch date', type: 'date' },
    { key: 'target_revenue', label: 'Target revenue', type: 'number' },
  ],
  'Business stability': [],
  'Expense Control': [],
  'Business Exit': [
    { key: 'target_valuation', label: 'Target valuation', type: 'number' },
    { key: 'target_exit_date', label: 'Target exit date', type: 'date' },
  ],
  'Vision Realization': [
    { key: 'current_vision', label: 'Current vision', type: 'text' },
    { key: 'emotional_impact', label: 'Emotional impact', type: 'text' },
  ],
};

function ScreenDomainStrategy({
  domain,
  domainIndex,
  totalDomains,
  data,
  domainConfig,
  formValues,
  setFormValues,
  onAdvance,
  submitting,
}: {
  domain: string;
  domainIndex: number;
  totalDomains: number;
  data: WorkshopObjectiveResponse;
  domainConfig: DomainConfig | null;
  formValues: Record<string, unknown>;
  setFormValues: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
  onAdvance: (r: Record<string, unknown>) => void;
  submitting: boolean;
}) {
  const strategies = domainConfig?.strategies?.[domain] ?? FALLBACK_DOMAIN_STRATEGIES[domain] ?? [];
  const fields = domainConfig?.fields?.[domain] ?? FALLBACK_DOMAIN_FIELDS[domain] ?? [];
  const existing = data?.domain_strategies?.find((ds) => ds.domain === domain);
  const formKey = `domain_strategy_${domain}`;
  const stored = (formValues[formKey] as Record<string, unknown>) ?? {};
  const strategiesVal = (stored.strategies as string[]) ?? existing?.strategies ?? [];
  const canAdvance = strategies.length === 0 || strategiesVal.length > 0;

  const handleAdvance = () => {
    const fieldValues: Record<string, unknown> = {};
    for (const f of fields) {
      const val = stored[f.key] ?? (existing as unknown as Record<string, unknown>)?.[f.key];
      if (val !== undefined && val !== null && val !== '') fieldValues[f.key] = val;
    }
    onAdvance({
      domain_strategy: {
        domain,
        strategies: strategiesVal,
        ...fieldValues,
      },
    });
  };

  return (
    <>
      <Text style={{ display: 'block', marginBottom: 16 }}>
        {domainIndex === 0
          ? `Let's define the strategy for ${domain}.`
          : `Strategy for ${domain} (${domainIndex + 1} of ${totalDomains}).`}
      </Text>
      <Form layout="vertical" style={{ marginBottom: 16 }}>
        {strategies.length > 0 && (
          <Form.Item label="Strategies" required>
            <Select
              mode="multiple"
              placeholder="Select strategies"
              options={strategies}
              value={strategiesVal}
              onChange={(v) =>
                setFormValues((p) => ({
                  ...p,
                  [formKey]: { ...(p[formKey] as object), strategies: v },
                }))
              }
              style={{ width: '100%' }}
            />
          </Form.Item>
        )}
        {fields.map((f) => (
          <Form.Item key={f.key} label={f.label}>
            {f.type === 'date' ? (
              <Input
                type="date"
                value={String((stored[f.key] ?? (existing as unknown as Record<string, unknown>)?.[f.key]) ?? '').slice(0, 10)}
                onChange={(e) =>
                  setFormValues((p) => ({
                    ...p,
                    [formKey]: { ...(p[formKey] as object), [f.key]: e.target.value || null },
                  }))
                }
              />
            ) : f.type === 'number' ? (
              <InputNumber
                style={{ width: '100%' }}
                value={(stored[f.key] ?? (existing as unknown as Record<string, unknown>)?.[f.key]) as number | undefined}
                onChange={(v) =>
                  setFormValues((p) => ({
                    ...p,
                    [formKey]: { ...(p[formKey] as object), [f.key]: v ?? null },
                  }))
                }
              />
            ) : (
              <TextArea
                rows={2}
                value={String((stored[f.key] ?? (existing as unknown as Record<string, unknown>)?.[f.key]) ?? '')}
                onChange={(e) =>
                  setFormValues((p) => ({
                    ...p,
                    [formKey]: { ...(p[formKey] as object), [f.key]: e.target.value || null },
                  }))
                }
              />
            )}
          </Form.Item>
        ))}
      </Form>
      <Button type="primary" onClick={handleAdvance} loading={submitting} disabled={!canAdvance}>
        Continue
      </Button>
    </>
  );
}

function Screen2VisionAndWhy({
  data,
  sessionId,
  formValues,
  setFormValues,
  onAdvance,
  submitting,
}: {
  data: WorkshopObjectiveResponse;
  sessionId: string;
  formValues: Record<string, unknown>;
  setFormValues: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
  onAdvance: (r: Record<string, unknown>) => void;
  submitting: boolean;
}) {
  const hasVision = !!data?.vision?.id;
  return (
    <>
      <Text style={{ display: 'block', marginBottom: 16 }}>
        Why is achieving this important to you?
      </Text>
      <Form layout="vertical" style={{ marginBottom: 16 }}>
        <Form.Item label="Vision description" required>
          <TextArea
            rows={3}
            value={String(formValues.vision_description ?? data?.vision?.vision_description ?? '')}
            onChange={(e) => setFormValues((p) => ({ ...p, vision_description: e.target.value }))}
            placeholder="Describe the future state you're aiming for"
          />
        </Form.Item>
        <Form.Item label="Emotional why" required>
          <TextArea
            rows={2}
            value={String(formValues.emotional_why ?? data?.objective?.emotional_why ?? '')}
            onChange={(e) => setFormValues((p) => ({ ...p, emotional_why: e.target.value }))}
            placeholder="Why does this matter personally?"
          />
        </Form.Item>
        <Form.Item label="Emotional importance (1=low, 10=high)" required>
          <Slider
            min={1}
            max={10}
            value={Number(formValues.emotional_importance_score ?? data?.objective?.emotional_importance_score ?? 5)}
            onChange={(v) => setFormValues((p) => ({ ...p, emotional_importance_score: v }))}
            marks={{ 1: 'low', 5: '5', 10: 'high' }}
          />
        </Form.Item>
      </Form>
      <Button
        type="primary"
        onClick={async () => {
          const visionDesc = formValues.vision_description ?? data?.vision?.vision_description ?? '';
          const emotionalWhy = formValues.emotional_why ?? data?.objective?.emotional_why ?? '';
          const score = formValues.emotional_importance_score ?? data?.objective?.emotional_importance_score ?? 5;
          if (visionDesc) {
            const desc = String(visionDesc);
            if (hasVision) {
              await updateWorkshopVision(sessionId, { vision_description: desc });
            } else {
              await createWorkshopVision(sessionId, { vision_description: desc });
            }
          }
          onAdvance({
            emotional_why: emotionalWhy,
            emotional_importance_score: typeof score === 'number' ? score : Number(score),
          });
        }}
        loading={submitting}
        disabled={
          !(formValues.vision_description ?? data?.vision?.vision_description) ||
          !(formValues.emotional_why ?? data?.objective?.emotional_why)
        }
      >
        Continue
      </Button>
    </>
  );
}

function Screen3ReportingPeriods({
  data,
  sessionId,
  formValues,
  setFormValues,
  onAdvance,
  submitting,
}: {
  data: WorkshopObjectiveResponse;
  sessionId: string;
  formValues: Record<string, unknown>;
  setFormValues: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
  onAdvance: (r: Record<string, unknown>) => void;
  submitting: boolean;
}) {
  const [periodType, setPeriodType] = useState<'LAST_YEAR' | 'LAST_QUARTER' | 'LAST_MONTH'>('LAST_YEAR');
  const ranges = getPeriodRanges(periodType);

  const [curValues, setCurValues] = useState({
    revenue: data?.current_period?.revenue ?? 0,
    expense: data?.current_period?.expense ?? 0,
    valuation: data?.current_period?.valuation ?? 0,
  });
  const [tgtValues, setTgtValues] = useState({
    revenue: data?.target_period?.revenue ?? 0,
    expense: data?.target_period?.expense ?? 0,
    valuation: data?.target_period?.valuation ?? 0,
  });

  useEffect(() => {
    setCurValues({
      revenue: data?.current_period?.revenue ?? 0,
      expense: data?.current_period?.expense ?? 0,
      valuation: data?.current_period?.valuation ?? 0,
    });
    setTgtValues({
      revenue: data?.target_period?.revenue ?? 0,
      expense: data?.target_period?.expense ?? 0,
      valuation: data?.target_period?.valuation ?? 0,
    });
  }, [data?.current_period, data?.target_period]);

  const toValuation = (v: unknown): number | undefined => {
    if (v === undefined || v === null) return undefined;
    const n = Number(v);
    return Number.isNaN(n) ? undefined : n;
  };

  const handleSubmit = async () => {
    const curPayload = {
      period_label: ranges.current.label,
      period_start: ranges.current.start,
      period_end: ranges.current.end,
      revenue: Number(curValues.revenue) || 0,
      expense: Number(curValues.expense) || 0,
      valuation: toValuation(curValues.valuation),
    };
    const tgtPayload = {
      period_label: ranges.target.label,
      period_start: ranges.target.start,
      period_end: ranges.target.end,
      revenue: Number(tgtValues.revenue) || 0,
      expense: Number(tgtValues.expense) || 0,
      valuation: toValuation(tgtValues.valuation),
    };
    const curId = data?.current_period?.id;
    const tgtId = data?.target_period?.id;
    if (curId) {
      await updateWorkshopBusinessPeriod(sessionId, curId, curPayload);
    } else {
      await createWorkshopBusinessPeriod(sessionId, { role: 'current', ...curPayload });
    }
    if (tgtId) {
      await updateWorkshopBusinessPeriod(sessionId, tgtId, tgtPayload);
    } else {
      await createWorkshopBusinessPeriod(sessionId, { role: 'target', ...tgtPayload });
    }
    onAdvance({});
  };

  return (
    <>
      <Text style={{ display: 'block', marginBottom: 16 }}>
        Let's anchor where you are now and what the target state looks like.
      </Text>
      <div style={{ marginBottom: 24 }}>
        <Form.Item label="Current reporting period">
          <Select
            options={[
              { value: 'LAST_YEAR', label: 'Last Year' },
              { value: 'LAST_QUARTER', label: 'Last Quarter' },
              { value: 'LAST_MONTH', label: 'Last Month' },
            ]}
            value={periodType}
            onChange={setPeriodType}
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          {ranges.current.start} – {ranges.current.end} → {ranges.target.start} – {ranges.target.end}
        </Text>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(80px, auto) 1fr 1fr',
          gap: '12px 24px',
          marginBottom: 24,
          alignItems: 'center',
        }}
      >
        <div />
        <div>
          <Text strong style={{ display: 'block' }}>
            Current
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {ranges.current.label}
          </Text>
        </div>
        <div>
          <Text strong style={{ display: 'block' }}>
            Future / Target
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {ranges.target.label}
          </Text>
        </div>
        <Text style={{ lineHeight: '32px' }}>Earnings</Text>
        <div style={{ lineHeight: '32px' }}>
          ${(Number(curValues.revenue || 0) - Number(curValues.expense || 0)).toLocaleString()}
        </div>
        <div style={{ lineHeight: '32px' }}>
          ${(Number(tgtValues.revenue || 0) - Number(tgtValues.expense || 0)).toLocaleString()}
          {(() => {
            const cur = Number(curValues.revenue || 0) - Number(curValues.expense || 0);
            const tgt = Number(tgtValues.revenue || 0) - Number(tgtValues.expense || 0);
            const pct = cur !== 0 ? (((tgt - cur) / cur) * 100).toFixed(1) : null;
            return pct != null ? (
              <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                {Number(pct) >= 0 ? '+' : ''}{pct}% growth
              </Text>
            ) : null;
          })()}
        </div>
        <Text style={{ lineHeight: '32px' }}>Revenue</Text>
        <Form.Item style={{ marginBottom: 16 }}>
          <InputNumber
            min={0}
            style={{ width: '100%' }}
            value={curValues.revenue}
            onChange={(v) => setCurValues((p) => ({ ...p, revenue: v ?? 0 }))}
            formatter={(v) => (v != null ? `$ ${Number(v).toLocaleString()}` : '')}
            parser={(v) => Number(String(v).replace(/[$,]/g, '')) || 0}
          />
        </Form.Item>
        <Form.Item style={{ marginBottom: 16 }}>
          <InputNumber
            min={0}
            style={{ width: '100%' }}
            value={tgtValues.revenue}
            onChange={(v) => setTgtValues((p) => ({ ...p, revenue: v ?? 0 }))}
            formatter={(v) => (v != null ? `$ ${Number(v).toLocaleString()}` : '')}
            parser={(v) => Number(String(v).replace(/[$,]/g, '')) || 0}
          />
          {Number(curValues.revenue || 0) !== 0 && (
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
              {(() => {
                const cur = Number(curValues.revenue || 0);
                const tgt = Number(tgtValues.revenue || 0);
                const pct = (((tgt - cur) / cur) * 100).toFixed(1);
                return `${Number(pct) >= 0 ? '+' : ''}${pct}% growth`;
              })()}
            </Text>
          )}
        </Form.Item>
        <Text style={{ lineHeight: '32px' }}>Expense</Text>
        <Form.Item style={{ marginBottom: 16 }}>
          <InputNumber
            min={0}
            style={{ width: '100%' }}
            value={curValues.expense}
            onChange={(v) => setCurValues((p) => ({ ...p, expense: v ?? 0 }))}
            formatter={(v) => (v != null ? `$ ${Number(v).toLocaleString()}` : '')}
            parser={(v) => Number(String(v).replace(/[$,]/g, '')) || 0}
          />
        </Form.Item>
        <Form.Item style={{ marginBottom: 16 }}>
          <InputNumber
            min={0}
            style={{ width: '100%' }}
            value={tgtValues.expense}
            onChange={(v) => setTgtValues((p) => ({ ...p, expense: v ?? 0 }))}
            formatter={(v) => (v != null ? `$ ${Number(v).toLocaleString()}` : '')}
            parser={(v) => Number(String(v).replace(/[$,]/g, '')) || 0}
          />
          {Number(curValues.expense || 0) !== 0 && (
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
              {(() => {
                const cur = Number(curValues.expense || 0);
                const tgt = Number(tgtValues.expense || 0);
                const pct = (((tgt - cur) / cur) * 100).toFixed(1);
                return `${Number(pct) >= 0 ? '+' : ''}${pct}% growth`;
              })()}
            </Text>
          )}
        </Form.Item>
        <Text style={{ lineHeight: '32px' }}>Valuation</Text>
        <Form.Item style={{ marginBottom: 0 }}>
          <InputNumber
            min={0}
            style={{ width: '100%' }}
            value={curValues.valuation}
            onChange={(v) => setCurValues((p) => ({ ...p, valuation: v ?? 0 }))}
            formatter={(v) => (v != null ? `$ ${Number(v).toLocaleString()}` : '')}
            parser={(v) => Number(String(v).replace(/[$,]/g, '')) || 0}
          />
        </Form.Item>
        <Form.Item style={{ marginBottom: 0 }}>
          <InputNumber
            min={0}
            style={{ width: '100%' }}
            value={tgtValues.valuation}
            onChange={(v) => setTgtValues((p) => ({ ...p, valuation: v ?? 0 }))}
            formatter={(v) => (v != null ? `$ ${Number(v).toLocaleString()}` : '')}
            parser={(v) => Number(String(v).replace(/[$,]/g, '')) || 0}
          />
          {Number(curValues.valuation || 0) !== 0 && (
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
              {(() => {
                const cur = Number(curValues.valuation || 0);
                const tgt = Number(tgtValues.valuation || 0);
                const pct = (((tgt - cur) / cur) * 100).toFixed(1);
                return `${Number(pct) >= 0 ? '+' : ''}${pct}% growth`;
              })()}
            </Text>
          )}
        </Form.Item>
      </div>
      <Button type="primary" onClick={handleSubmit} loading={submitting}>
        Continue
      </Button>
    </>
  );
}

function Screen4TimelineConfidence({
  data,
  formValues,
  setFormValues,
  onAdvance,
  submitting,
}: {
  data: WorkshopObjectiveResponse;
  formValues: Record<string, unknown>;
  setFormValues: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
  onAdvance: (r: Record<string, unknown>) => void;
  submitting: boolean;
}) {
  return (
    <>
      <Text style={{ display: 'block', marginBottom: 16 }}>
        At your current pace, when do you get there?
      </Text>
      <Form layout="vertical" style={{ marginBottom: 16 }}>
        <Form.Item label="Estimated months to goal" required>
          <InputNumber
            min={1}
            style={{ width: '100%' }}
            value={Number(formValues.estimated_months_to_goal ?? data?.objective?.estimated_months_to_goal ?? 12)}
            onChange={(v) => setFormValues((p) => ({ ...p, estimated_months_to_goal: v }))}
          />
        </Form.Item>
        <Form.Item label="Confidence (1-10)" required>
          <Slider
            min={1}
            max={10}
            value={Number(formValues.confidence_score ?? data?.objective?.confidence_score ?? 5)}
            onChange={(v) => setFormValues((p) => ({ ...p, confidence_score: v }))}
            marks={{ 1: '1', 5: '5', 10: '10' }}
          />
        </Form.Item>
      </Form>
      <Button
        type="primary"
        onClick={() =>
          onAdvance({
            estimated_months_to_goal: formValues.estimated_months_to_goal ?? data?.objective?.estimated_months_to_goal ?? 12,
            confidence_score: formValues.confidence_score ?? data?.objective?.confidence_score ?? 5,
          })
        }
        loading={submitting}
        disabled={
          !(formValues.estimated_months_to_goal ?? data?.objective?.estimated_months_to_goal)
        }
      >
        Continue
      </Button>
    </>
  );
}

function formatDelayCategory(cat: string): string {
  return cat.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function Screen5Delays({
  data,
  sessionId,
  refreshData,
  formValues,
  setFormValues,
  onAdvance,
  submitting,
}: {
  data: WorkshopObjectiveResponse;
  sessionId: string;
  refreshData: () => Promise<void>;
  formValues: Record<string, unknown>;
  setFormValues: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
  onAdvance: (r: Record<string, unknown>) => void;
  submitting: boolean;
}) {
  const [newDesc, setNewDesc] = useState('');
  const [phase, setPhase] = useState<'selecting' | 'adding' | 'categorizing'>('selecting');
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(new Set());
  const delays = data?.delays ?? [];

  const rawDomain = formValues.objective_domain ?? data?.objective?.objective_domain;
  const objectiveDomainForForm = Array.isArray(rawDomain)
    ? rawDomain.join(', ')
    : (rawDomain as string | null | undefined);
  const domainStrategiesFromForm =
    domainStrategiesFromFormValues(formValues, objectiveDomainForForm) ?? [];
  const persistedDomainStrategies = (data?.domain_strategies ?? []).filter(
    (ds) => (ds.strategies?.length ?? 0) > 0 || ds.domain
  );
  const domainStrategyByKey = new Map<string, NonNullable<WorkshopObjectiveResponse['domain_strategies']>[number]>();
  for (const ds of [...persistedDomainStrategies, ...domainStrategiesFromForm]) {
    domainStrategyByKey.set(ds.domain, ds);
  }
  const domainStrategies = Array.from(domainStrategyByKey.values()).filter(
    (ds) => (ds.strategies?.length ?? 0) > 0 || ds.domain
  );
  const hasDomainStrategies = domainStrategies.length > 0;

  useEffect(() => {
    const existingCats = (data?.delays ?? []).map((d) => d.delay_category).filter(Boolean) as string[];
    if (existingCats.length > 0) {
      setSelectedCategories((prev) => (prev.size === 0 ? new Set(existingCats) : prev));
    }
  }, [data?.delays]);

  const handleCategoryToggle = (cat: string, checked: boolean) => {
    setSelectedCategories((prev) => {
      const next = new Set(prev);
      if (checked) next.add(cat);
      else next.delete(cat);
      return next;
    });
  };

  const handleDoneSelecting = async () => {
    const existingCats = new Set(delays.map((d) => d.delay_category).filter(Boolean));
    for (const cat of Array.from(selectedCategories)) {
      if (!existingCats.has(cat)) {
        await createWorkshopDelay(sessionId, {
          description: formatDelayCategory(cat),
          delay_category: cat,
          impact_score: 5,
        });
        existingCats.add(cat);
      }
    }
    for (const d of delays) {
      if (d.delay_category && !selectedCategories.has(d.delay_category)) {
        await deleteWorkshopDelay(sessionId, d.id);
      }
    }
    await refreshData();
    setPhase('categorizing');
  };

  const handleAdd = async () => {
    if (!newDesc.trim()) return;
    await createWorkshopDelay(sessionId, {
      description: newDesc.trim(),
      delay_category: 'OTHER',
      impact_score: 5,
    });
    setNewDesc('');
    await refreshData();
  };

  const handleRemove = async (delayId: string) => {
    await deleteWorkshopDelay(sessionId, delayId);
    await refreshData();
  };

  const handleCategorize = async (delayId: string, category: string, impactScore: number) => {
    await updateWorkshopDelay(sessionId, delayId, {
      delay_category: category,
      impact_score: impactScore,
    });
    await refreshData();
  };

  const handleDoneAdding = () => {
    setPhase('categorizing');
  };

  const handleAddAnother = () => {
    setPhase('adding');
  };

  return (
    <>
      {hasDomainStrategies && (
        <div style={{ marginBottom: 24 }}>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>
            Your selected domains and strategies
          </Text>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {domainStrategies.map((ds) => (
              <li key={ds.domain} style={{ marginBottom: 4 }}>
                <Text strong>{ds.domain}</Text>
                {(ds.strategies?.length ?? 0) > 0 && (
                  <Text type="secondary">: {(ds.strategies ?? []).join(', ')}</Text>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <Text style={{ display: 'block', marginBottom: 16 }}>
        {hasDomainStrategies
          ? "Why aren't these strategies implemented right now? What is blocking you from achieving these objectives?"
          : "Why can't you get there faster? What is blocking you from achieving your objectives?"}
      </Text>

      {phase === 'selecting' && (
        <>
          <div style={{ marginBottom: 24 }}>
            <Text strong style={{ display: 'block', marginBottom: 12 }}>
              Select the delay categories that apply
            </Text>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {DELAY_CATEGORIES.map((cat) => (
                <Checkbox
                  key={cat}
                  checked={selectedCategories.has(cat)}
                  onChange={(e) => handleCategoryToggle(cat, e.target.checked)}
                >
                  {formatDelayCategory(cat)}
                </Checkbox>
              ))}
            </div>
          </div>
          <Button
            type="primary"
            onClick={handleDoneSelecting}
            loading={submitting}
            disabled={selectedCategories.size === 0}
          >
            Continue
          </Button>
        </>
      )}

      {phase === 'adding' && (
        <>
          {/* Phase 1: Add all delay descriptions */}
          <div style={{ marginBottom: 24 }}>
            <Text strong>Identify delays</Text>
            <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
              List each delay or bottleneck. You'll categorize them in the next step.
            </Text>
            <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <Input
                placeholder="Describe the delay or bottleneck..."
                value={newDesc}
                onChange={(e) => setNewDesc(e.target.value)}
                onPressEnter={handleAdd}
                style={{ flex: 1, minWidth: 200 }}
              />
              <Button onClick={handleAdd} disabled={!newDesc.trim() || submitting}>
                Add
              </Button>
            </div>
          </div>

          {delays.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <Text strong>Your delays ({delays.length})</Text>
              <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                {delays.map((d) => (
                  <li key={d.id} style={{ marginBottom: 4 }}>
                    {d.description ?? '—'}
                    <Button type="link" size="small" danger onClick={() => handleRemove(d.id)} style={{ marginLeft: 8 }}>
                      Remove
                    </Button>
                  </li>
                ))}
              </ul>
              <Button type="primary" onClick={handleDoneAdding} style={{ marginTop: 12 }}>
                I've identified all delays
              </Button>
            </div>
          )}
        </>
      )}

      {phase === 'categorizing' && (
        <>
          {/* Phase 2: Categorize and score */}
          <div style={{ marginBottom: 24 }}>
            <Text strong>Review & categorize</Text>
            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
              Assign a category and impact score (1–10) for each delay.
            </Text>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {delays.map((d) => (
                <DelayReviewRow
                  key={d.id}
                  delay={d}
                  onRemove={() => handleRemove(d.id)}
                  onCategorize={(cat, score) => handleCategorize(d.id, cat, score)}
                  submitting={submitting}
                />
              ))}
            </div>
            <div style={{ marginTop: 8, display: 'flex', gap: 16 }}>
              <Button type="link" onClick={handleAddAnother}>
                Add another delay
              </Button>
              <Button
                type="link"
                onClick={() => {
                  setSelectedCategories(new Set(delays.map((d) => d.delay_category).filter(Boolean) as string[]));
                  setPhase('selecting');
                }}
              >
                Change category selection
              </Button>
            </div>
          </div>

          <Button
            type="primary"
            onClick={() => onAdvance({})}
            loading={submitting}
            disabled={delays.length === 0}
          >
            Continue
          </Button>
        </>
      )}
    </>
  );
}

function DelayReviewRow({
  delay,
  onRemove,
  onCategorize,
  submitting,
}: {
  delay: { id: string; description?: string | null; delay_category?: string | null; impact_score?: number | null };
  onRemove: () => void;
  onCategorize: (category: string, impactScore: number) => void;
  submitting: boolean;
}) {
  const [category, setCategory] = useState(delay.delay_category ?? 'OTHER');
  const [impactScore, setImpactScore] = useState(delay.impact_score ?? 5);

  useEffect(() => {
    setCategory(delay.delay_category ?? 'OTHER');
    setImpactScore(delay.impact_score ?? 5);
  }, [delay.delay_category, delay.impact_score]);

  const handleCategoryChange = (v: string) => {
    setCategory(v);
    onCategorize(v, impactScore);
  };

  const handleImpactAfterChange = (v: number) => {
    const score = v ?? 5;
    setImpactScore(score);
    onCategorize(category, score);
  };

  const handleApply = () => {
    onCategorize(category, impactScore);
  };

  const isCategorized = delay.delay_category && delay.delay_category !== 'OTHER';

  return (
    <Card size="small" style={{ backgroundColor: isCategorized ? undefined : 'rgba(0,0,0,0.02)' }}>
      <div style={{ marginBottom: 8 }}>
        <Text strong>{delay.description ?? '—'}</Text>
        <Button type="link" size="small" danger onClick={onRemove} style={{ float: 'right' }}>
          Remove
        </Button>
      </div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <Select
          style={{ minWidth: 180 }}
          value={category}
          onChange={handleCategoryChange}
          options={DELAY_CATEGORIES.map((c) => ({ value: c, label: c.replace(/_/g, ' ') }))}
          placeholder="Category"
        />
        <span style={{ display: 'inline-block', minWidth: 200 }}>
          <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Impact (1=low, 10=high)</Text>
          <Slider
            min={1}
            max={10}
            value={impactScore}
            onChange={(v) => setImpactScore(v ?? 5)}
            onAfterChange={handleImpactAfterChange}
            marks={{ 1: 'low', 5: '5', 10: 'high' }}
          />
        </span>
        <Button size="small" onClick={handleApply} disabled={submitting}>
          {isCategorized ? 'Update' : 'Categorize'}
        </Button>
      </div>
    </Card>
  );
}

/**
 * Baseline projection using monthly compounding.
 * r = (V1/V0)^(1/months_to_goal) - 1
 * V(t) = V0 * (1+r)^t for t <= months_to_goal
 *
 * For Future+1 and Future+2 we flatten growth: early-stage ramp rates (e.g. 20%/mo
 * for $30K→$350K) should not compound indefinitely. We use a capped "mature" rate
 * (2%/mo ≈ 27% annual) for periods beyond the target.
 *
 * Time points: Now (t=0), Future (t=months_to_goal), Future+1 (t+12), Future+2 (t+24)
 */
const MATURE_MONTHLY_GROWTH = 0.06; // 6%/mo ≈ 101% annual post-target growth (5–8% range)

function projectBaseline(
  v0: number,
  v1: number,
  monthsToGoal: number
): { baseline: number[]; monthlyRate: number } {
  const t0 = 0;
  const t1 = monthsToGoal;
  if (v0 === 0) {
    return {
      baseline: [v0, v1, v1, v1],
      monthlyRate: 0,
    };
  }
  const monthlyRate = Math.pow(v1 / v0, 1 / monthsToGoal) - 1;
  const factor = 1 + monthlyRate;
  // Current and Future: use full ramp
  const vFuture = v0 * Math.pow(factor, t1);
  // Future+1, Future+2: flatten with mature rate (avoid 10x+ compounding)
  const matureFactor = 1 + MATURE_MONTHLY_GROWTH;
  const baseline = [
    v0 * Math.pow(factor, t0),
    vFuture,
    vFuture * Math.pow(matureFactor, 12),
    vFuture * Math.pow(matureFactor, 24),
  ];
  return { baseline, monthlyRate };
}

/**
 * Accelerated path: same growth curve, shifted by monthsSaved earlier.
 * For ramp period (index 1): shift = (1+r)^monthsSaved.
 * For post-target (indices 2,3): shift = (1+matureRate)^monthsSaved.
 */
function projectAccelerated(
  baseline: number[],
  monthlyRate: number,
  monthsSaved: number,
  v0: number,
  useMatureRateForPostTarget = true
): number[] {
  if (v0 === 0 || monthsSaved === 0) return [...baseline];
  const rampShift = Math.pow(1 + monthlyRate, monthsSaved);
  const matureShift = useMatureRateForPostTarget
    ? Math.pow(1 + MATURE_MONTHLY_GROWTH, monthsSaved)
    : rampShift;
  return baseline.map((v, i) => {
    if (i === 0) return v0;
    if (i === 1) return v * rampShift;
    return v * matureShift;
  });
}

function computeAccelerationFinancials(args: {
  curPeriod: WorkshopObjectiveResponse['current_period'];
  tgtPeriod: WorkshopObjectiveResponse['target_period'];
  monthsEst: number;
  effectiveMonthsSaved: number;
  accelValue: number | null;
}): {
  potential_months_saved: number;
  financial_delta: number | null;
  value_per_month: number | null;
  estimated_acceleration_value: number | null;
} {
  const { curPeriod, tgtPeriod, monthsEst, effectiveMonthsSaved, accelValue } = args;
  const v0 = Number(curPeriod?.valuation ?? 0);
  const v1 = Number(tgtPeriod?.valuation ?? 0);
  const curEarnings = Number(curPeriod?.revenue ?? 0) - Number(curPeriod?.expense ?? 0);
  const tgtEarnings = Number(tgtPeriod?.revenue ?? 0) - Number(tgtPeriod?.expense ?? 0);
  let financialDelta: number | null = null;
  if (v0 > 0 || v1 > 0) {
    financialDelta = v1 - v0;
  } else if (monthsEst > 0) {
    financialDelta = (tgtEarnings - curEarnings) * monthsEst;
  }
  const valuePerMonth =
    financialDelta != null && monthsEst > 0 ? financialDelta / monthsEst : null;
  return {
    potential_months_saved: effectiveMonthsSaved,
    financial_delta: financialDelta,
    value_per_month: valuePerMonth,
    estimated_acceleration_value: accelValue,
  };
}

const barHeight = 100;
function GroupedBarChart({
  values,
  colors,
  maxScale,
  labels,
  fmt,
  fmtFull,
}: {
  values: number[];
  colors: string[];
  maxScale: number;
  labels: string[];
  fmt: (n: number) => string;
  fmtFull?: (n: number) => string;
}) {
  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end', flex: 1, minWidth: 0 }}>
      {values.map((v, i) => (
        <div
          key={i}
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            minWidth: 20,
          }}
        >
          <span style={{ fontSize: 10 }} title={fmtFull ? fmtFull(v) : undefined}>{fmt(v)}</span>
          <div
            style={{
              width: '100%',
              height: barHeight,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              borderRadius: 4,
            }}
          >
            <div
              style={{
                height: `${Math.max(2, (v / maxScale) * 100)}%`,
                minHeight: v > 0 ? 4 : 0,
                backgroundColor: colors[i],
                borderRadius: '4px 4px 0 0',
                width: '100%',
              }}
              title={labels[i]}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function Screen6AccelerationModel({
  sessionId,
  data,
  loadData,
  formValues,
  setFormValues,
  onAdvance,
  submitting,
}: {
  sessionId: string;
  data: WorkshopObjectiveResponse;
  loadData: () => Promise<void>;
  formValues: Record<string, unknown>;
  setFormValues: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
  onAdvance: (r: Record<string, unknown>) => void;
  submitting: boolean;
}) {
  const obj = data?.objective;
  const monthsEst = Number(obj?.estimated_months_to_goal ?? 12) || 12;
  const curPeriod = data?.current_period;
  const tgtPeriod = data?.target_period;
  const R0 = Number(curPeriod?.revenue ?? 0);
  const E0 = Number(curPeriod?.expense ?? 0);
  const V0 = Number(curPeriod?.valuation ?? 0);
  const R1 = Number(tgtPeriod?.revenue ?? 0);
  const E1 = Number(tgtPeriod?.expense ?? 0);
  const V1 = Number(tgtPeriod?.valuation ?? 0);

  const { baseline: revBase, monthlyRate: rRev } = projectBaseline(R0, R1, monthsEst);
  const { baseline: expBase, monthlyRate: rExp } = projectBaseline(E0, E1, monthsEst);
  const { baseline: valBase, monthlyRate: rVal } = projectBaseline(V0, V1, monthsEst);

  const sliderMax = 10;
  const monthsSaved =
    Number(formValues.potential_months_saved ?? obj?.potential_months_saved ?? 0) ?? 0;
  const sliderValue = Math.round((monthsSaved / monthsEst) * sliderMax);
  const effectiveMonthsSaved = parseFloat(((sliderValue / sliderMax) * monthsEst).toFixed(1));

  const revAccel = projectAccelerated(revBase, rRev, effectiveMonthsSaved, R0);
  const expAccel = projectAccelerated(expBase, rExp, effectiveMonthsSaved, E0);
  const valAccel = projectAccelerated(valBase, rVal, effectiveMonthsSaved, V0);

  // Acceleration value: compounding delta at target date
  // V_accel(months_to_goal) - V_base(months_to_goal) = V1 * ((1+r)^months_saved - 1)
  const accelValue =
    V1 > 0 && rVal > -1
      ? V1 * (Math.pow(1 + rVal, effectiveMonthsSaved) - 1)
      : null;

  const handleSliderChange = async (v: number) => {
    const months = parseFloat(((v / sliderMax) * monthsEst).toFixed(1));
    setFormValues((p) => ({ ...p, potential_months_saved: months }));
    await updateWorkshopObjective(sessionId, { potential_months_saved: months });
    await loadData();
  };

  const delays = data?.delays ?? [];
  const sortedDelays = [...delays].sort((a, b) => (b.impact_score ?? 0) - (a.impact_score ?? 0));

  const fmt = (n: number) =>
    n >= 1_000_000 ? `$${(n / 1e6).toFixed(1)}M` : `$${n.toLocaleString()}`;
  const fmtCompact = (n: number): string => {
    if (n >= 1e6) return `$${n >= 10e6 ? Math.round(n / 1e6) : (n / 1e6).toFixed(1)}M`;
    if (n >= 1e3) return `$${n >= 10e3 ? Math.round(n / 1e3) : (n / 1e3).toFixed(1)}K`;
    return `$${Math.round(n)}`;
  };
  const fmtChartLabel = (n: number) => {
    const s = fmtCompact(n);
    return s.length > 5 ? s.slice(0, 5) : s;
  };
  const maxRevExp = Math.max(...revBase, ...revAccel, ...expBase, ...expAccel, 1);
  const maxVal = Math.max(...valBase, ...valAccel, 1);

  const periodLabels = ['Current', 'Future', 'Future+1', 'Future+2'];
  const revExpColors = ['rgba(82, 196, 26, 0.4)', 'rgba(82, 196, 26, 0.9)', 'rgba(255, 77, 79, 0.4)', 'rgba(255, 77, 79, 0.9)'];
  const valColors = ['rgba(24, 144, 255, 0.4)', 'rgba(24, 144, 255, 0.9)'];

  return (
    <>
      <Text style={{ display: 'block', marginBottom: 16 }}>
        If we removed the biggest delay, how much time could we realistically recover?
      </Text>
      {sortedDelays.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <Text strong>Your delays (by impact)</Text>
          <ul style={{ marginTop: 8, paddingLeft: 20 }}>
            {sortedDelays.map((d) => (
              <li key={d.id}>
                {d.description ?? '—'}
                {d.impact_score != null && (
                  <Text type="secondary" style={{ marginLeft: 8 }}>
                    (impact: {d.impact_score})
                  </Text>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div style={{ marginBottom: 24 }}>
        <Text strong style={{ display: 'block', marginBottom: 12 }}>
          Baseline vs accelerated (shifted {effectiveMonthsSaved} mo sooner)
        </Text>
        <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12 }}>
          Ramp: r = (target/current)^(1/months) − 1. Future+1/+2 use a mature growth rate (~100% annual) to avoid unrealistic compounding. Accelerated = curve shifted by months saved.
        </Text>

        <div style={{ marginBottom: 20 }}>
          <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>
            Revenue & Expense
          </Text>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            {periodLabels.map((label, i) => (
              <div key={i} style={{ flex: 1, minWidth: 100 }}>
                <Text type="secondary" style={{ display: 'block', textAlign: 'center', marginBottom: 4, fontSize: 12 }}>
                  {label}
                </Text>
                <GroupedBarChart
                  values={[revBase[i], revAccel[i], expBase[i], expAccel[i]]}
                  colors={revExpColors}
                  maxScale={maxRevExp}
                  labels={['Rev base', 'Rev accel', 'Exp base', 'Exp accel']}
                  fmt={fmtChartLabel}
                  fmtFull={fmt}
                />
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 11 }}>
            <span><span style={{ color: '#95de64' }}>■</span> Rev base</span>
            <span><span style={{ color: '#389e0d' }}>■</span> Rev accel</span>
            <span><span style={{ color: '#ffa39e' }}>■</span> Exp base</span>
            <span><span style={{ color: '#cf1322' }}>■</span> Exp accel</span>
          </div>
        </div>

        <div>
          <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>
            Valuation
          </Text>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            {periodLabels.map((label, i) => (
              <div key={i} style={{ flex: 1, minWidth: 100 }}>
                <Text type="secondary" style={{ display: 'block', textAlign: 'center', marginBottom: 4, fontSize: 12 }}>
                  {label}
                </Text>
                <GroupedBarChart
                  values={[valBase[i], valAccel[i]]}
                  colors={valColors}
                  maxScale={maxVal}
                  labels={['Val base', 'Val accel']}
                  fmt={fmtChartLabel}
                  fmtFull={fmt}
                />
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 11 }}>
            <span><span style={{ color: '#69c0ff' }}>■</span> Val base</span>
            <span><span style={{ color: '#096dd9' }}>■</span> Val accel</span>
          </div>
        </div>
      </div>

      <div style={{ marginBottom: 24 }}>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>
          Potential months saved
        </Text>
        <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12 }}>
          As the change starts sooner, the same growth curve yields more value (area under the curve increases).
        </Text>
        <Slider
          min={0}
          max={sliderMax}
          value={sliderValue}
          onChange={(v) => {
            setFormValues((p) => ({ ...p, potential_months_saved: ((v ?? 0) / sliderMax) * monthsEst }));
          }}
          onAfterChange={(v) => handleSliderChange(v ?? 0)}
          marks={Object.fromEntries(
            [...Array(sliderMax + 1)].map((_, i) => [i, i === 0 ? '0' : i === sliderMax ? `${monthsEst}` : ''])
          )}
        />
        <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between' }}>
          <Text type="secondary">{effectiveMonthsSaved} months</Text>
          {accelValue != null && (
            <Text strong>Acceleration value: {fmt(accelValue)}</Text>
          )}
        </div>
      </div>

      <Button
        type="primary"
        onClick={() =>
          onAdvance(
            computeAccelerationFinancials({
              curPeriod,
              tgtPeriod,
              monthsEst,
              effectiveMonthsSaved,
              accelValue,
            })
          )
        }
        loading={submitting}
      >
        Continue
      </Button>
    </>
  );
}

function Screen7ReadinessGate({
  data,
  formValues,
  setFormValues,
  onAdvance,
  submitting,
}: {
  data: WorkshopObjectiveResponse;
  formValues: Record<string, unknown>;
  setFormValues: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
  onAdvance: (r: Record<string, unknown>) => void;
  submitting: boolean;
}) {
  const obj = data?.objective;
  const cur = data?.current_period;
  const tgt = data?.target_period;
  const monthsEst = Number(obj?.estimated_months_to_goal ?? 12) || 12;
  const valuePerDay =
    obj?.financial_delta != null && monthsEst > 0
      ? obj.financial_delta / (monthsEst * 30)
      : null;
  const fmt = (n: number | null | undefined) =>
    n != null
      ? n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0, minimumFractionDigits: 0 })
      : '—';

  return (
    <>
      <Text style={{ display: 'block', marginBottom: 16 }}>
        Does shortening the timeline matter?
      </Text>

      <section className={styles.bridgeObjectiveSection} style={{ marginBottom: 24 }}>
        <div className={styles.bridgeStrip}>
          {/* Current State */}
          <div className={styles.bridgeZoneCurrent}>
            <div className={styles.bridgeZoneLabel}>Current state</div>
            <div className={styles.compactFields}>
              <div className={styles.compactRow}>
                <span className={styles.compactLabel}>Period</span>
                <span>{cur?.period_label ?? '—'}</span>
              </div>
              <div className={styles.compactRow}>
                <span className={styles.compactLabel}>Revenue</span>
                <span>{fmt(cur?.revenue)}</span>
              </div>
              <div className={styles.compactRow}>
                <span className={styles.compactLabel}>Expense</span>
                <span>{fmt(cur?.expense)}</span>
              </div>
              <div className={styles.compactRow}>
                <span className={styles.compactLabel}>Net income</span>
                <span>{fmt(cur?.net_income)}</span>
              </div>
              <div className={styles.compactRow} style={{ marginTop: 4, fontWeight: 600 }}>
                <span className={styles.compactLabel}>Valuation</span>
                <span>{fmt(cur?.valuation)}</span>
              </div>
            </div>
          </div>

          <div className={styles.bridgeConnector} aria-hidden>
            <span className={styles.bridgeConnectorArrow}>→</span>
            <span className={styles.bridgeConnectorText}>Transition</span>
          </div>

          {/* Transition */}
          <div className={styles.bridgeZoneBridge}>
            <div className={styles.bridgeZoneLabel}>Transition</div>
            <div className={styles.compactFields}>
              <div className={styles.compactRow}>
                <span className={styles.compactLabel}>Months to goal</span>
                <span>{obj?.estimated_months_to_goal != null ? obj.estimated_months_to_goal : '—'}</span>
              </div>
              <div className={styles.compactRow}>
                <span className={styles.compactLabel}>Value per day</span>
                <span>{valuePerDay != null ? fmt(valuePerDay) : '—'}</span>
              </div>
              <div className={styles.compactRow}>
                <span className={styles.compactLabel}>Months saved (target)</span>
                <span>
                  {obj?.potential_months_saved != null
                    ? parseFloat(Number(obj.potential_months_saved).toFixed(1))
                    : '—'}
                </span>
              </div>
              <div className={styles.compactRow} style={{ marginTop: 4, fontWeight: 600 }}>
                <span className={styles.compactLabel}>Acceleration value</span>
                <span>{fmt(obj?.estimated_acceleration_value)}</span>
              </div>
              {(data?.delays?.length ?? 0) > 0 && (
                <div className={styles.compactRow} style={{ marginTop: 8, flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                  <span className={styles.compactLabel}>Delays</span>
                  <ul style={{ margin: 0, paddingLeft: 16, fontSize: 11 }}>
                    {data.delays.map((d) => (
                      <li key={d.id}>
                        {d.description ?? formatDelayCategory(d.delay_category)}
                        {d.description && (
                          <span style={{ color: '#8c8c8c', marginLeft: 4 }}>
                            — {formatDelayCategory(d.delay_category)}
                          </span>
                        )}
                        {d.impact_score != null && (
                          <span style={{ color: '#8c8c8c', marginLeft: 4 }}>
                            (impact: {d.impact_score})
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          <div className={styles.bridgeConnector} aria-hidden>
            <span className={styles.bridgeConnectorArrow}>→</span>
            <span className={styles.bridgeConnectorText}>Future</span>
          </div>

          {/* Future State */}
          <div className={styles.bridgeZoneFuture}>
            <div className={styles.bridgeZoneLabel}>Future state</div>
            <div className={styles.compactFields}>
              <div className={styles.compactRow}>
                <span className={styles.compactLabel}>Period</span>
                <span>{tgt?.period_label ?? '—'}</span>
              </div>
              <div className={styles.compactRow}>
                <span className={styles.compactLabel}>Revenue</span>
                <span>{fmt(tgt?.revenue)}</span>
              </div>
              <div className={styles.compactRow}>
                <span className={styles.compactLabel}>Expense</span>
                <span>{fmt(tgt?.expense)}</span>
              </div>
              <div className={styles.compactRow}>
                <span className={styles.compactLabel}>Net income</span>
                <span>{fmt(tgt?.net_income)}</span>
              </div>
              <div className={styles.compactRow} style={{ marginTop: 4, fontWeight: 600 }}>
                <span className={styles.compactLabel}>Valuation</span>
                <span>{fmt(tgt?.valuation)}</span>
              </div>
              {(data?.domain_strategies?.length ?? 0) > 0 && (
                <div className={styles.compactRow} style={{ marginTop: 8, flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                  <span className={styles.compactLabel}>Domains & strategies</span>
                  <ul style={{ margin: 0, paddingLeft: 16, fontSize: 11 }}>
                    {(data.domain_strategies ?? []).map((ds) => (
                      <li key={ds.id}>
                        <strong>{formatDelayCategory(ds.domain)}</strong>
                        {ds.strategies?.length ? `: ${ds.strategies.join(', ')}` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <div style={{ marginBottom: 16 }}>
        <Button
          type={formValues.timeline_shortening_importance === 'YES_URGENT' ? 'primary' : 'default'}
          style={{ marginRight: 8, marginBottom: 8 }}
          onClick={() => setFormValues((p) => ({ ...p, timeline_shortening_importance: 'YES_URGENT' }))}
        >
          Yes, urgent
        </Button>
        <Button
          type={formValues.timeline_shortening_importance === 'YES_BUT_CAUTIOUS' ? 'primary' : 'default'}
          style={{ marginRight: 8, marginBottom: 8 }}
          onClick={() => setFormValues((p) => ({ ...p, timeline_shortening_importance: 'YES_BUT_CAUTIOUS' }))}
        >
          Yes, but cautious
        </Button>
        <Button
          type={formValues.timeline_shortening_importance === 'NOT_URGENT' ? 'primary' : 'default'}
          style={{ marginBottom: 8 }}
          onClick={() => setFormValues((p) => ({ ...p, timeline_shortening_importance: 'NOT_URGENT' }))}
        >
          Not urgent
        </Button>
      </div>
      <Button
        type="primary"
        onClick={() => onAdvance({ timeline_shortening_importance: formValues.timeline_shortening_importance })}
        loading={submitting}
        disabled={!formValues.timeline_shortening_importance}
      >
        Continue
      </Button>
    </>
  );
}

const NOT_URGENT_CLOSING_SECTION = `Thank you again for taking the time to work through your objective.

Whether or not accelerating the timeline feels important right now, the most critical step has already happened:

The objective is defined.

Clarity changes the system.

Before an objective is defined, progress is reactive.
After it is defined, decisions can be aligned.

Even if you choose to move at the current pace, the act of defining the destination gives you something most businesses never fully establish:

• A measurable target
• A time horizon
• A value attached to that time
• A way to evaluate daily decisions against the future you want

Acceleration is not about rushing.
It's about removing friction.

If at any point you decide the timeline matters more — whether due to market opportunity, competitive pressure, capital timing, or personal goals — we can step into the Business Domain and identify where the leverage actually lives.

That process is not about working harder.
It's about identifying which 2–4 variables meaningfully change the curve.

For now, you have clarity.
That alone moves you forward.

When you're ready to explore how to reduce friction and compress the timeline intentionally, the door is open.`;

function Screen8ObjectiveSummary({
  data,
  sessionId,
  onContinue,
  submitting,
}: {
  data: WorkshopObjectiveResponse;
  sessionId: string;
  onContinue: () => void;
  submitting: boolean;
}) {
  const obj = data?.objective;
  const cur = data?.current_period;
  const tgt = data?.target_period;
  const monthsEst = Number(obj?.estimated_months_to_goal ?? 12) || 12;
  const valuePerDay =
    obj?.financial_delta != null && monthsEst > 0
      ? obj.financial_delta / (monthsEst * 30)
      : null;
  const fmt = (n: number | null | undefined) =>
    n != null
      ? n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0, minimumFractionDigits: 0 })
      : '—';

  const buildEmailBody = () => {
    const lines: string[] = [];
    lines.push('YOUR DEFINED OBJECTIVE');
    lines.push('—'.repeat(40));
    lines.push('');
    lines.push(`Objective: ${obj?.objective_description ?? '—'}`);
    lines.push(`Domain: ${obj?.objective_domain ?? '—'}`);
    lines.push(`Target date: ${obj?.target_date ?? '—'}`);
    lines.push(`Vision: ${data?.vision?.vision_description ?? '—'}`);
    lines.push(`Emotional why: ${obj?.emotional_why ?? '—'}`);
    lines.push('');
    lines.push('CURRENT STATE');
    lines.push(`Period: ${cur?.period_label ?? '—'}`);
    lines.push(`Revenue: ${fmt(cur?.revenue)} | Expense: ${fmt(cur?.expense)} | Valuation: ${fmt(cur?.valuation)}`);
    lines.push('');
    lines.push('TRANSITION');
    lines.push(`Months to goal: ${obj?.estimated_months_to_goal ?? '—'}`);
    lines.push(`Value per day: ${valuePerDay != null ? fmt(valuePerDay) : '—'}`);
    lines.push(`Months saved (target): ${obj?.potential_months_saved != null ? parseFloat(Number(obj.potential_months_saved).toFixed(1)) : '—'}`);
    lines.push(`Acceleration value: ${fmt(obj?.estimated_acceleration_value)}`);
    if ((data?.delays?.length ?? 0) > 0) {
      lines.push('Delays:');
      data.delays.forEach((d) => {
        const desc = d.description ?? formatDelayCategory(d.delay_category);
        const cat = formatDelayCategory(d.delay_category);
        const impact = d.impact_score != null ? ` (impact: ${d.impact_score})` : '';
        lines.push(`  • ${desc}${d.description ? ` — ${cat}` : ''}${impact}`);
      });
    }
    lines.push('');
    lines.push('FUTURE STATE');
    lines.push(`Period: ${tgt?.period_label ?? '—'}`);
    lines.push(`Revenue: ${fmt(tgt?.revenue)} | Expense: ${fmt(tgt?.expense)} | Valuation: ${fmt(tgt?.valuation)}`);
    if ((data?.domain_strategies?.length ?? 0) > 0) {
      lines.push('Domains & strategies:');
      (data.domain_strategies ?? []).forEach((ds) => {
        const strategies = ds.strategies?.length ? `: ${ds.strategies.join(', ')}` : '';
        lines.push(`  • ${formatDelayCategory(ds.domain)}${strategies}`);
      });
    }
    lines.push('');
    lines.push('—'.repeat(40));
    lines.push('');
    lines.push(NOT_URGENT_CLOSING_SECTION);
    return lines.join('\n');
  };

  const handleEmailObjective = () => {
    const subject = 'Your Defined Objective — and What It Unlocks';
    const body = buildEmailBody();
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <>
      <Text style={{ display: 'block', marginBottom: 16 }}>
        Here's what we documented today.
      </Text>

      <section className={styles.bridgeObjectiveSection} style={{ marginBottom: 24 }}>
        <div className={styles.bridgeStrip}>
          {/* Current State */}
          <div className={styles.bridgeZoneCurrent}>
            <div className={styles.bridgeZoneLabel}>Current state</div>
            <div className={styles.compactFields}>
              <div className={styles.compactRow}>
                <span className={styles.compactLabel}>Period</span>
                <span>{cur?.period_label ?? '—'}</span>
              </div>
              <div className={styles.compactRow}>
                <span className={styles.compactLabel}>Revenue</span>
                <span>{fmt(cur?.revenue)}</span>
              </div>
              <div className={styles.compactRow}>
                <span className={styles.compactLabel}>Expense</span>
                <span>{fmt(cur?.expense)}</span>
              </div>
              <div className={styles.compactRow}>
                <span className={styles.compactLabel}>Net income</span>
                <span>{fmt(cur?.net_income)}</span>
              </div>
              <div className={styles.compactRow} style={{ marginTop: 4, fontWeight: 600 }}>
                <span className={styles.compactLabel}>Valuation</span>
                <span>{fmt(cur?.valuation)}</span>
              </div>
            </div>
          </div>

          <div className={styles.bridgeConnector} aria-hidden>
            <span className={styles.bridgeConnectorArrow}>→</span>
            <span className={styles.bridgeConnectorText}>Transition</span>
          </div>

          {/* Transition */}
          <div className={styles.bridgeZoneBridge}>
            <div className={styles.bridgeZoneLabel}>Transition</div>
            <div className={styles.compactFields}>
              <div className={styles.compactRow}>
                <span className={styles.compactLabel}>Months to goal</span>
                <span>{obj?.estimated_months_to_goal != null ? obj.estimated_months_to_goal : '—'}</span>
              </div>
              <div className={styles.compactRow}>
                <span className={styles.compactLabel}>Value per day</span>
                <span>{valuePerDay != null ? fmt(valuePerDay) : '—'}</span>
              </div>
              <div className={styles.compactRow}>
                <span className={styles.compactLabel}>Months saved (target)</span>
                <span>
                  {obj?.potential_months_saved != null
                    ? parseFloat(Number(obj.potential_months_saved).toFixed(1))
                    : '—'}
                </span>
              </div>
              <div className={styles.compactRow} style={{ marginTop: 4, fontWeight: 600 }}>
                <span className={styles.compactLabel}>Acceleration value</span>
                <span>{fmt(obj?.estimated_acceleration_value)}</span>
              </div>
              {(data?.delays?.length ?? 0) > 0 && (
                <div className={styles.compactRow} style={{ marginTop: 8, flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                  <span className={styles.compactLabel}>Delays</span>
                  <ul style={{ margin: 0, paddingLeft: 16, fontSize: 11 }}>
                    {data.delays.map((d) => (
                      <li key={d.id}>
                        {d.description ?? formatDelayCategory(d.delay_category)}
                        {d.description && (
                          <span style={{ color: '#8c8c8c', marginLeft: 4 }}>
                            — {formatDelayCategory(d.delay_category)}
                          </span>
                        )}
                        {d.impact_score != null && (
                          <span style={{ color: '#8c8c8c', marginLeft: 4 }}>
                            (impact: {d.impact_score})
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          <div className={styles.bridgeConnector} aria-hidden>
            <span className={styles.bridgeConnectorArrow}>→</span>
            <span className={styles.bridgeConnectorText}>Future</span>
          </div>

          {/* Future State */}
          <div className={styles.bridgeZoneFuture}>
            <div className={styles.bridgeZoneLabel}>Future state</div>
            <div className={styles.compactFields}>
              <div className={styles.compactRow}>
                <span className={styles.compactLabel}>Period</span>
                <span>{tgt?.period_label ?? '—'}</span>
              </div>
              <div className={styles.compactRow}>
                <span className={styles.compactLabel}>Revenue</span>
                <span>{fmt(tgt?.revenue)}</span>
              </div>
              <div className={styles.compactRow}>
                <span className={styles.compactLabel}>Expense</span>
                <span>{fmt(tgt?.expense)}</span>
              </div>
              <div className={styles.compactRow}>
                <span className={styles.compactLabel}>Net income</span>
                <span>{fmt(tgt?.net_income)}</span>
              </div>
              <div className={styles.compactRow} style={{ marginTop: 4, fontWeight: 600 }}>
                <span className={styles.compactLabel}>Valuation</span>
                <span>{fmt(tgt?.valuation)}</span>
              </div>
              {(data?.domain_strategies?.length ?? 0) > 0 && (
                <div className={styles.compactRow} style={{ marginTop: 8, flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                  <span className={styles.compactLabel}>Domains & strategies</span>
                  <ul style={{ margin: 0, paddingLeft: 16, fontSize: 11 }}>
                    {(data.domain_strategies ?? []).map((ds) => (
                      <li key={ds.id}>
                        <strong>{formatDelayCategory(ds.domain)}</strong>
                        {ds.strategies?.length ? `: ${ds.strategies.join(', ')}` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <div style={{ marginBottom: 24, padding: 16, backgroundColor: '#fafafa', borderRadius: 8, fontSize: 13, lineHeight: 1.7 }}>
        <Text strong style={{ display: 'block', marginBottom: 12 }}>
          Closing Section for Your Email
        </Text>
        <Text type="secondary" style={{ whiteSpace: 'pre-line' }}>
          {NOT_URGENT_CLOSING_SECTION}
        </Text>
      </div>

      <Button
        type="primary"
        icon={<MailOutlined />}
        onClick={handleEmailObjective}
        loading={submitting}
        size="large"
      >
        Email objective
      </Button>
    </>
  );
}

const NEXT_STEPS_EXPLANATION = `We address acceleration by first defining the objective — which we have just done.

Next we diagnose the current state to identify friction points to speed. Then we come up with focused actions to reduce those friction points.

Steps 2 and 3 are part of the Bridge Session. For that we charge a toll of one hour of the time value and 1–2 hours of your time.`;

function Screen9CommitmentToll({
  sessionId,
  data,
  loadData,
  formValues,
  setFormValues,
  onAdvance,
  submitting,
}: {
  sessionId: string;
  data: WorkshopObjectiveResponse;
  loadData: () => Promise<void>;
  formValues: Record<string, unknown>;
  setFormValues: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
  onAdvance: (r: Record<string, unknown>) => void;
  submitting: boolean;
}) {
  const obj = data?.objective;
  const cur = data?.current_period;
  const tgt = data?.target_period;
  const vision = data?.vision;
  const impliedToll =
    obj?.financial_delta != null &&
    obj?.estimated_months_to_goal != null &&
    obj.estimated_months_to_goal > 0
      ? Math.round(obj.financial_delta / (obj.estimated_months_to_goal * 30 * 8))
      : null;
  const tollValue =
    formValues.commitment_toll != null
      ? Number(formValues.commitment_toll)
      : obj?.commitment_toll ?? impliedToll ?? 0;

  const fmt = (n: number | null | undefined) =>
    n != null ? n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0, minimumFractionDigits: 0 }) : '—';

  const handleTollSave = async (v: number) => {
    await updateWorkshopObjective(sessionId, { commitment_toll: v });
    await loadData();
  };

  return (
    <>
      <div style={{ marginBottom: 24, padding: 16, backgroundColor: '#f6ffed', borderRadius: 8, fontSize: 13, lineHeight: 1.7, border: '1px solid #b7eb8f' }}>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>Next steps</Text>
        <Text style={{ whiteSpace: 'pre-line' }}>{NEXT_STEPS_EXPLANATION}</Text>
      </div>

      <section className={styles.bridgeObjectiveSection} style={{ marginBottom: 24 }}>
        <div className={styles.bridgeStrip}>
          {/* Current State */}
          <div className={styles.bridgeZoneCurrent}>
            <div className={styles.bridgeZoneLabel}>Current state</div>
            <div className={styles.compactFields}>
              <div className={styles.compactRow}>
                <span className={styles.compactLabel}>Period</span>
                <span>{cur?.period_label ?? '—'}</span>
              </div>
              <div className={styles.compactRow}>
                <span className={styles.compactLabel}>Revenue</span>
                <span>{fmt(cur?.revenue)}</span>
              </div>
              <div className={styles.compactRow}>
                <span className={styles.compactLabel}>Expense</span>
                <span>{fmt(cur?.expense)}</span>
              </div>
              <div className={styles.compactRow}>
                <span className={styles.compactLabel}>Net income</span>
                <span>{fmt(cur?.net_income)}</span>
              </div>
              <div className={styles.compactRow} style={{ marginTop: 4, fontWeight: 600 }}>
                <span className={styles.compactLabel}>Valuation</span>
                <span>{fmt(cur?.valuation)}</span>
              </div>
            </div>
          </div>

          <div className={styles.bridgeConnector} aria-hidden>
            <span className={styles.bridgeConnectorArrow}>→</span>
            <span className={styles.bridgeConnectorText}>Bridge</span>
          </div>

          {/* Transition — with commitment toll in center */}
          <div className={styles.bridgeZoneBridge}>
            <div className={styles.bridgeZoneLabel}>Transition</div>
            <div className={styles.compactFields}>
              <div className={styles.compactRow}>
                <span className={styles.compactLabel}>Months to goal</span>
                <span>{obj?.estimated_months_to_goal != null ? obj.estimated_months_to_goal : '—'}</span>
              </div>
              <div className={styles.compactRow}>
                <span className={styles.compactLabel}>Value per month</span>
                <span>{fmt(obj?.value_per_month)}</span>
              </div>
              <div className={styles.compactRow}>
                <span className={styles.compactLabel}>Months saved (target)</span>
                <span>
                  {obj?.potential_months_saved != null
                    ? parseFloat(Number(obj.potential_months_saved).toFixed(1))
                    : '—'}
                </span>
              </div>
              <div className={styles.compactRow} style={{ marginTop: 4, fontWeight: 600 }}>
                <span className={styles.compactLabel}>Acceleration value</span>
                <span>{fmt(obj?.estimated_acceleration_value)}</span>
              </div>
              <div className={styles.compactRow} style={{ marginTop: 12, flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                <span className={styles.compactLabel}>Commitment toll (1 hr value)</span>
                <InputNumber
                  value={tollValue}
                  onChange={(v) => setFormValues((p) => ({ ...p, commitment_toll: v ?? impliedToll ?? 0 }))}
                  onBlur={() => handleTollSave(tollValue)}
                  min={0}
                  formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, '') as unknown as number}
                  style={{ fontSize: 18, fontWeight: 600, width: '100%' }}
                />
              </div>
              <div className={styles.compactRow} style={{ marginTop: 4 }}>
                <span className={styles.compactLabel}>1–2 hours of your time</span>
              </div>
            </div>
          </div>

          <div className={styles.bridgeConnector} aria-hidden>
            <span className={styles.bridgeConnectorArrow}>→</span>
            <span className={styles.bridgeConnectorText}>Future</span>
          </div>

          {/* Future State */}
          <div className={styles.bridgeZoneFuture}>
            <div className={styles.bridgeZoneLabel}>Future state</div>
            <div className={styles.compactFields}>
              <div className={styles.compactRow}>
                <span className={styles.compactLabel}>Period</span>
                <span>{tgt?.period_label ?? '—'}</span>
              </div>
              <div className={styles.compactRow}>
                <span className={styles.compactLabel}>Revenue</span>
                <span>{fmt(tgt?.revenue)}</span>
              </div>
              <div className={styles.compactRow}>
                <span className={styles.compactLabel}>Expense</span>
                <span>{fmt(tgt?.expense)}</span>
              </div>
              <div className={styles.compactRow}>
                <span className={styles.compactLabel}>Net income</span>
                <span>{fmt(tgt?.net_income)}</span>
              </div>
              <div className={styles.compactRow} style={{ marginTop: 4, fontWeight: 600 }}>
                <span className={styles.compactLabel}>Valuation</span>
                <span>{fmt(tgt?.valuation)}</span>
              </div>
              <div className={styles.compactRow} style={{ marginTop: 8 }}>
                <span className={styles.compactLabel}>Financial delta</span>
                <span style={{ fontWeight: 600 }}>{fmt(obj?.financial_delta)}</span>
              </div>
              <div style={{ marginTop: 12, fontSize: 12 }}>
                <span className={styles.compactLabel} style={{ display: 'block', marginBottom: 4 }}>Objective</span>
                <Text>{obj?.objective_description ?? '—'}</Text>
              </div>
              {vision?.vision_description && (
                <div style={{ marginTop: 8, fontSize: 11 }}>
                  <span className={styles.compactLabel} style={{ display: 'block', marginBottom: 2 }}>Vision</span>
                  <Text type="secondary">{vision.vision_description}</Text>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <div style={{ marginBottom: 16 }}>
        <Button
          type="primary"
          size="large"
          icon={<CheckOutlined />}
          style={{ marginRight: 8 }}
          onClick={() => onAdvance({ toll_status: 'ACCEPTED', commitment_toll: tollValue })}
          loading={submitting}
        >
          Accept
        </Button>
        <Button style={{ marginRight: 8 }} onClick={() => onAdvance({ toll_status: 'DEFERRED', commitment_toll: tollValue })} loading={submitting}>
          Defer
        </Button>
        <Button onClick={() => onAdvance({ toll_status: 'DECLINED', commitment_toll: tollValue })} loading={submitting}>
          Decline
        </Button>
      </div>
    </>
  );
}

function Screen10WhatHappensNext({
  sessionId,
  onContinue,
  submitting,
}: {
  sessionId: string;
  onContinue: () => void;
  submitting: boolean;
}) {
  return (
    <>
      <Text style={{ display: 'block', marginBottom: 16 }}>
        Bridge Session begins. You'll answer structured questions to identify the highest-leverage frictions.
      </Text>
      <Button type="primary" size="large" onClick={onContinue} loading={submitting} icon={<CheckOutlined />}>
        Start Bridge Session
      </Button>
    </>
  );
}
